import { Download } from 'lucide-react';
import { ItemListProvider } from '@/components/itemList/ItemListProvider';
import { FileRow } from '@/components/itemList/FileRow';
import type { NormalizedItem } from '@/components/itemList/types';
import { useItemActions } from '@/hooks/useItemActions';
import { Button } from '@/components/ui/Button';

interface LinkFileListProps {
  items: NormalizedItem[];
  onRefresh: () => void;
  updateIsQuerying: (querying: boolean) => void;
  onClickItem: (item: NormalizedItem) => void;
}

export function LinkFileList({ items, onRefresh, updateIsQuerying, onClickItem }: LinkFileListProps) {
  // The link's signed URLs are attachment URLs, so recipients can download without workspace access
  const { downloadItem, downloadItems } = useItemActions({ updateIsQuerying, onRefresh });

  return (
    <ItemListProvider
      items={items}
      userRole="viewer"
      useCase="link"
      onRefresh={onRefresh}
      updateIsQuerying={updateIsQuerying}
    >
      {items.length > 1 && (
        <div className="mb-2 flex justify-end">
          <Button variant="secondary" size="sm" onClick={() => downloadItems(items)}>
            <Download className="mr-1.5 size-4" /> Download all ({items.length})
          </Button>
        </div>
      )}
      <ul className="space-y-1 p-0 m-0 list-none">
        {items.map((item) => (
          <FileRow
            key={item.id}
            item={item}
            variant="minimal"
            onClickItem={onClickItem}
            actions={
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadItem(item);
                }}
                className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-accent-foreground"
                title="Download"
                aria-label={`Download ${item.title || item.name}`}
              >
                <Download className="h-4 w-4" />
              </button>
            }
          />
        ))}
      </ul>
    </ItemListProvider>
  );
}
